import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import PageWrapper from 'components/UI/PageWrapper';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import {
  fetchDevicesInRollout,
  fetchGroupsInRollout,
  getRollout,
  rolloutDetailsSelector,
  setDeviceFilters as setRolloutDeviceFilters,
} from 'store/rolloutState/rolloutState';
import Loader from 'components/UI/Loader';
import Card from 'components/UI/Card';
import { CardHeaderTop, CardHeaderTopColumn } from '../styled';
import Heading, { Type as HeadingType } from 'components/UI/Heading';
import { FormattedMessage } from 'react-intl';
import RolloutInfoCard from 'components/rollout/RolloutInfoCard';
import Pagination, { PagePagination } from 'components/UI/Pagination';
import CardContainer from 'components/UI/CardContainer';
import { SortOrder } from 'utils/array';
import RolloutGroupTable from 'components/rollout/RolloutGroupTable';
import { useAccess } from 'hooks/useAccess';
import RolloutDeviceTable from 'components/rollout/RolloutDeviceTable';
import { DeviceFilter } from 'models/rollout';
import { ButtonGrid } from 'styles';
import Button from 'components/UI/Button';
import FoldableCard from 'components/UI/FoldableCard';
import RolloutDeviceFilters from 'components/rollout/RolloutDeviceFilters';
import { metaSelector } from 'store';
// Initial pagination
const initialPagination = {
  limit: 10,
  offset: 0,
  page: 1,
};

const RolloutDetailsPage: FC = () => {
  const dispatch = useAppDispatch();
  const { id } = useParams();

  const { isLoading, data, groups, devices } = useAppSelector(
    rolloutDetailsSelector
  );
  const meta = useAppSelector(metaSelector);
  const { adminAccess } = useAccess();

  // Fetch rollout details if id selected
  useEffect(() => {
    if (id) {
      dispatch(getRollout(id));
      dispatch(fetchGroupsInRollout(id, 0));
      dispatch(fetchDevicesInRollout(id, [], 0));
    }
  }, [dispatch, id]);

  // Fetch groups in rollout
  const fetchGroups = useCallback(
    (offset?: number) => {
      if (id) {
        dispatch(fetchGroupsInRollout(id, offset ?? 0));
      }
    },
    [dispatch, id]
  );

  // Fetch devices in rollout
  const fetchDevices = useCallback(
    (deviceFilters: DeviceFilter[], offset?: number) => {
      if (id) {
        dispatch(fetchDevicesInRollout(id, deviceFilters, offset ?? 0));
      }
    },
    [dispatch, id]
  );

  // Toggle filters
  const toggleFilters = useCallback(() => {
    dispatch(setRolloutDeviceFilters(!devices.filters.active));
  }, [dispatch, devices.filters.active]);

  // Apply filters
  const onFilter = useCallback(
    (deviceFilters: DeviceFilter[]) => {
      setPagination(initialPagination);
      fetchDevices(deviceFilters, initialPagination.offset);
    },
    [fetchDevices]
  );

  // State
  const [pagination, setPagination] =
    useState<PagePagination>(initialPagination);

  // Paginate
  const onSetPagination = useCallback(
    (paginate: PagePagination) => {
      setPagination(paginate);
      fetchDevices(devices.filters.data, paginate.offset);
    },
    [fetchDevices, devices]
  );

  // Paginate
  const onSetPaginationGroups = useCallback(
    (paginate: PagePagination) => {
      setPagination(paginate);
      fetchGroups(paginate.offset);
    },
    [fetchGroups]
  );

  // Reset pagination
  const resetPagination = useCallback(
    () => setPagination(initialPagination),
    []
  );

  // Render results
  const groupResults = useMemo(() => {
    if (groups.isLoading) {
      return <Loader center />;
    }
    if (!id || !groups.data || groups.data.length === 0) {
      return null;
    }
    return (
      <RolloutGroupTable
        groups={groups.data}
        rolloutId={id}
        initialActiveColumn={2}
        initialSortOrder={SortOrder.Asc}
      />
    );
  }, [groups, id]);

  const devicesResults = useMemo(() => {
    if (devices.isLoading) {
      return <Loader center />;
    }
    if (
      (data?.attributes?.status === 'creating' ||
        data?.attributes?.status === 'ready') &&
      devices?.data?.length === 0
    ) {
      return <FormattedMessage id="rollout.no_devices_info" />;
    }
    if (!devices.data || devices.data.length === 0) {
      return null;
    }
    return <RolloutDeviceTable devices={devices} />;
  }, [devices, data]);

  if (isLoading) {
    return <Loader center />;
  }
  if (!id) {
    return null;
  }
  return (
    <PageWrapper>
      <RolloutInfoCard rollout={data} isLoading={isLoading} />

      <FoldableCard open={devices.filters.active}>
        <Heading type={HeadingType.h3} uppercase>
          <FormattedMessage id="rollout.filter_title" />
        </Heading>

        <RolloutDeviceFilters
          onFilter={onFilter}
          onClear={resetPagination}
          data={meta.data}
        />
      </FoldableCard>

      <CardContainer>
        <Card>
          <CardHeaderTop>
            <CardHeaderTopColumn>
              <Heading type={HeadingType.h3} uppercase>
                <FormattedMessage id="rollout.devices" />
              </Heading>
            </CardHeaderTopColumn>

            <ButtonGrid>
              <Button
                onClick={toggleFilters}
                backgroundColor="surface"
                color={devices.filters.active ? 'primaryDark' : 'onSurface'}
              >
                <FormattedMessage id="filters.btn" />
              </Button>
            </ButtonGrid>
          </CardHeaderTop>
          {devicesResults}
          <Pagination
            totalCount={devices.totalCount || 0}
            noItems={devices.data?.length}
            pagination={pagination}
            setPagination={onSetPagination}
          />
        </Card>
      </CardContainer>
      {adminAccess && (
        <CardContainer>
          <Card>
            <CardHeaderTop>
              <CardHeaderTopColumn>
                <Heading type={HeadingType.h3} uppercase>
                  <FormattedMessage id="rollout.groups" />
                </Heading>
              </CardHeaderTopColumn>
            </CardHeaderTop>
            {groupResults}
            <Pagination
              totalCount={groups?.totalCount || 0}
              noItems={groups.data?.length}
              pagination={pagination}
              setPagination={onSetPaginationGroups}
            />
          </Card>
        </CardContainer>
      )}
    </PageWrapper>
  );
};
export default RolloutDetailsPage;
